import React, { useEffect, useState, useRef, useMemo } from "react"
import axios from "axios"
import clsx from "clsx"
import { FormikProps } from "formik"

interface HierarchyPerson {
     label: string
     value: number
     descendant_ids: number[]
     has_children: boolean
     children?: HierarchyPerson[]
}

interface Props {
     name: string
     label: string
     endpoint: string
     formikProps: FormikProps<any>
     classNameLayout?: string
}

const normalizeString = (str: string) =>
     str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()

const KpiHierarchySelectField = ({ name, label, endpoint, formikProps, classNameLayout }: Props) => {
     const [isOpen, setIsOpen] = useState(false)
     const [rawData, setRawData] = useState<HierarchyPerson[]>([])
     const [loading, setLoading] = useState(false)
     const [searchTerm, setSearchTerm] = useState("")
     const [expandedIds, setExpandedIds] = useState<number[]>([])

     const dropdownRef = useRef<HTMLDivElement>(null)
     const selectedIds: number[] = formikProps.values[name] || []

     // Determine if field has error to apply styling
     const isInvalid = formikProps.touched[name] && formikProps.errors[name]

     // --- NEW LOGIC: Dynamic Label Formatting ---
     const displayLabel = useMemo(() => {
          if (selectedIds.length === 0) return "Choisir les responsables..."

          // Get labels for selected IDs
          const selectedLabels = rawData.filter(p => selectedIds.includes(p.value)).map(p => p.label)

          // Fallback if data isn't loaded yet but IDs exist
          if (selectedLabels.length === 0) return `${selectedIds.length} sélectionnés`

          const count = selectedLabels.length

          if (count === 1) return selectedLabels[0]
          if (count === 2) return `${selectedLabels[0]}, ${selectedLabels[1]}`

          // For 3 or more: First two + remaining count
          return `${selectedLabels[0]}, ${selectedLabels[1]} +${count - 2}`
     }, [selectedIds, rawData])

     useEffect(() => {
          setLoading(true)
          axios.post(endpoint, {})
               .then(res => {
                    setRawData(res.data)
                    const allIds = res.data.map((p: HierarchyPerson) => p.value)
                    setExpandedIds(allIds)
               })
               .finally(() => setLoading(false))
     }, [endpoint])

     const filteredTree = useMemo(() => {
          if (!rawData.length) return []

          const build = (data: HierarchyPerson[]) => {
               const allDescendants = new Set(data.flatMap(p => p.descendant_ids))
               const roots = data.filter(p => !allDescendants.has(p.value))

               const mapChildren = (parent: HierarchyPerson): HierarchyPerson => ({
                    ...parent,
                    children: data
                         .filter(
                              p =>
                                   parent.descendant_ids.includes(p.value) &&
                                   !data.some(other => other !== parent && parent.descendant_ids.includes(other.value) && other.descendant_ids.includes(p.value))
                         )
                         .map(mapChildren),
               })
               return roots.map(mapChildren)
          }

          const tree = build(rawData)
          if (!searchTerm) return tree

          const filterNode = (node: HierarchyPerson): HierarchyPerson | null => {
               const normalizedNode = normalizeString(node.label)
               const normalizedSearch = normalizeString(searchTerm)
               const matches = normalizedNode.includes(normalizedSearch)
               const filteredChildren = node.children?.map(filterNode).filter(Boolean) as HierarchyPerson[]

               if (matches || filteredChildren.length > 0) return { ...node, children: filteredChildren }
               return null
          }
          return tree.map(filterNode).filter(Boolean) as HierarchyPerson[]
     }, [rawData, searchTerm])

     const toggleExpand = (e: React.MouseEvent, id: number) => {
          e.stopPropagation()
          setExpandedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]))
     }

     const toggleId = (id: number) => {
          const newIds = selectedIds.includes(id) ? selectedIds.filter(i => i !== id) : [...selectedIds, id]
          formikProps.setFieldValue(name, Array.from(new Set(newIds)))
          formikProps.setFieldTouched(name, true)
     }

     const toggleTeam = (e: React.MouseEvent, person: HierarchyPerson) => {
          e.stopPropagation()
          const teamIds = [person.value, ...person.descendant_ids]
          const allTeamSelected = teamIds.every(id => selectedIds.includes(id))
          const newIds = allTeamSelected ? selectedIds.filter(id => !teamIds.includes(id)) : [...selectedIds, ...teamIds]
          formikProps.setFieldValue(name, Array.from(new Set(newIds)))
          formikProps.setFieldTouched(name, true)
     }

     const renderBranch = (node: HierarchyPerson, level = 0) => {
          const isExpanded = expandedIds.includes(node.value)
          const isSelected = selectedIds.includes(node.value)
          const isTeamSelected = [node.value, ...node.descendant_ids].every(id => selectedIds.includes(id))

          return (
               <div key={node.value} className="d-flex flex-column">
                    <div
                         className="d-flex align-items-center justify-content-between py-2 px-3 rounded bg-hover-light cursor-pointer group border-bottom border-gray-100 last:border-0"
                         style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
                         onClick={() => toggleId(node.value)}
                    >
                         <div className="d-flex align-items-center gap-2">
                              <div
                                   className="w-30px h-30px d-flex align-items-center justify-content-center cursor-pointer bg-hover-light-primary rounded"
                                   onClick={e => toggleExpand(e, node.value)}
                              >
                                   {node.has_children && <i className={clsx("fa-solid fa-caret-right fs-7 text-gray-400 rotate-transition", isExpanded && "rotate-90")} />}
                              </div>

                              <div className="form-check form-check-custom form-check-solid form-check-sm">
                                   <input className="form-check-input" type="checkbox" checked={isSelected} readOnly />
                                   <label className={clsx("form-check-label fs-7 cursor-pointer ms-2", isSelected ? "text-primary fw-bold" : "text-gray-700")}>{node.label}</label>
                              </div>
                         </div>

                         {node.has_children && (
                              <span
                                   className={clsx(
                                        "fs-9 fw-boldest px-2 py-1 rounded transition-all",
                                        isTeamSelected ? "bg-primary text-white" : "text-primary bg-light-primary border border-primary border-dashed"
                                   )}
                                   onClick={e => toggleTeam(e, node)}
                              >
                                   {isTeamSelected ? "ÉQUIPE ✔" : "+ ÉQUIPE"}
                              </span>
                         )}
                    </div>

                    {node.has_children && isExpanded && <div className="ms-5 border-start border-gray-200">{node.children?.map(child => renderBranch(child, level + 1))}</div>}
               </div>
          )
     }

     useEffect(() => {
          const handleClickOutside = (e: MouseEvent) => {
               if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                    if (isOpen) {
                         setIsOpen(false)
                         formikProps.setFieldTouched(name, true)
                    }
               }
          }
          document.addEventListener("mousedown", handleClickOutside)
          return () => document.removeEventListener("mousedown", handleClickOutside)
     }, [isOpen, name, formikProps])

     return (
          <div className={clsx("position-relative w-100", classNameLayout)} ref={dropdownRef}>
               {label && <label className="fs-6 fw-bolder text-gray-900 mb-2 d-block">{label}</label>}

               <div
                    className={clsx("form-control form-control-solid d-flex align-items-center justify-content-between cursor-pointer border-hover-primary", {
                         "border-danger": isInvalid,
                    })}
                    onClick={() => setIsOpen(!isOpen)}
               >
                    {/* UPDATED LABEL DISPLAY */}
                    <span className="text-gray-700 fs-7 fw-bold text-truncate pe-5">{displayLabel}</span>
                    <i className={clsx("fa-solid fa-chevron-down fs-9 text-gray-400 transition-all", isOpen && "rotate-180")}></i>
               </div>

               {isOpen && (
                    <div className="card shadow-lg position-absolute w-100 mt-1 border border-gray-200 animate__animated animate__fadeIn animate__faster" style={{ zIndex: 1200 }}>
                         <div className="p-4 border-bottom border-gray-100">
                              <div className="input-group input-group-sm input-group-solid">
                                   <span className="input-group-text">
                                        <i className="fa-solid fa-magnifying-glass fs-7" />
                                   </span>
                                   <input
                                        type="text"
                                        className="form-control fs-7"
                                        placeholder="Filtrer..."
                                        autoFocus
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                   />
                              </div>
                         </div>

                         <div className="card-body p-2" style={{ maxHeight: "350px", overflowY: "auto" }}>
                              {loading ? (
                                   <div className="text-center py-10">
                                        <span className="spinner-border spinner-border-sm text-primary"></span>
                                   </div>
                              ) : filteredTree.length > 0 ? (
                                   filteredTree.map(root => renderBranch(root))
                              ) : (
                                   <div className="text-center py-5 text-muted fs-7">Aucun résultat</div>
                              )}
                         </div>

                         <div className="p-2 bg-light border-top border-gray-200 rounded-bottom">
                              <div className="d-flex flex-stack gap-3 mb-3">
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-light-success flex-root border border-dashed border-success"
                                        onClick={() => {
                                             formikProps.setFieldValue(
                                                  name,
                                                  rawData.map(p => p.value)
                                             )
                                             formikProps.setFieldTouched(name, true)
                                        }}
                                   >
                                        Tout cocher
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger flex-root border border-dashed border-danger"
                                        onClick={() => {
                                             formikProps.setFieldValue(name, [])
                                             formikProps.setFieldTouched(name, true)
                                        }}
                                   >
                                        Tout décocher
                                   </button>
                              </div>
                              <button
                                   type="button"
                                   className="btn btn-primary btn-sm w-100 fw-bolder"
                                   onClick={() => {
                                        setIsOpen(false)
                                        formikProps.setFieldTouched(name, true)
                                   }}
                              >
                                   Valider la sélection
                              </button>
                         </div>
                    </div>
               )}
          </div>
     )
}

export default KpiHierarchySelectField
